<template>
  <div class="set-theory-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 基础集合框 (全集 U) -->
           <rect x="150" y="80" width="500" height="400" fill="none" stroke="rgba(255,255,255,0.2)" stroke-dasharray="8,6" rx="16" stroke-width="2" />
           <text x="170" y="110" fill="#94a3b8" font-size="20" font-weight="900">全集 U</text>
           
           <!-- 模式 1: 集合运算 (Set Operations) -->
           <g v-if="mode === 'set'">
              <!-- 集合 A -->
              <circle :cx="400 - offset" cy="300" r="140" :fill="fillA" stroke="#38bdf8" stroke-width="4" />
              <text :x="320 - offset" y="280" fill="#f8fafc" font-size="40" font-weight="900">A</text>
              
              <!-- 集合 B -->
              <circle :cx="400 + offset" cy="300" r="140" :fill="fillB" stroke="#f43f5e" stroke-width="4" />
              <text :x="480 + offset" y="280" fill="#f8fafc" font-size="40" font-weight="900">B</text>
              
              <!-- 交集高亮遮罩模拟 -->
              <g v-if="op === 'intersection' && offset < 140" clip-path="url(#clipA)">
                 <circle :cx="400 + offset" cy="300" r="140" fill="rgba(16, 185, 129, 0.8)" stroke="#10b981" stroke-width="3" />
              </g>
              <defs>
                 <clipPath id="clipA"><circle :cx="400 - offset" cy="300" r="140" /></clipPath>
              </defs>
           </g>

           <!-- 模式 2: 充分必要条件 (Condition) -->
           <g v-else-if="mode === 'condition'">
              <!-- A 是 B 的充分条件 => A ⊆ B -->
              <!-- 集合 B (大结果) -->
              <circle cx="440" cy="280" r="180" fill="rgba(244, 63, 94, 0.2)" stroke="#f43f5e" stroke-width="4" />
              <text x="560" y="200" fill="#fb7185" font-size="24" font-weight="900">后果/集合 B</text>
              
              <!-- 集合 A (前置条件), 可以被拖拽进去或出来 -->
              <circle :cx="440 - condOffset" cy="280" r="100" fill="rgba(56, 189, 248, 0.4)" stroke="#38bdf8" stroke-width="4" />
              <text :x="420 - condOffset" y="290" fill="#bae6fd" font-size="24" font-weight="900">条件 A</text>

              <!-- 指示连线与解释 -->
              <g v-if="condOffset === 0">
                 <!-- 完全包含 (充分条件成立) -->
                 <path d="M 440 280 L 440 380 L 520 440" fill="none" stroke="#10b981" stroke-width="4" marker-end="url(#arrow-set)" />
                 <text x="540" y="450" fill="#34d399" font-size="24" font-weight="bold">A 发生，则 B 必然发生 (充分)</text>
              </g>
              <g v-else>
                 <text x="250" y="480" fill="#facc15" font-size="20">A 部分在 B 之外，此时 A 并非 B 的充分条件</text>
              </g>
           </g>

           <!-- 模式 3: 量词 (Quantifier) -->
           <g v-else-if="mode === 'quantifier'">
              <circle cx="400" cy="300" r="180" fill="rgba(71, 85, 105, 0.2)" stroke="#94a3b8" stroke-width="3" />
              <text x="350" y="160" fill="#cbd5e1" font-size="20" font-weight="bold">论域集合 X</text>

              <!-- 散落的元素点 -->
              <g v-for="i in 15" :key="'p'+i" :transform="`translate(${400 + Math.cos(i)*120}, ${300 + Math.sin(i*2)*110})`">
                 <!-- universal: 全选; existential: 选几个 -->
                 <circle cx="0" cy="0" r="12" :fill="isSatisfied(i) ? '#10b981' : '#f43f5e'" />
                 <text x="0" y="5" fill="#fff" font-size="14" text-anchor="middle" font-weight="bold">{{ i }}</text>
              </g>

              <g transform="translate(400, 530)">
                 <text v-if="qType === 'universal'" x="0" y="0" fill="#34d399" font-size="24" font-weight="900" text-anchor="middle">
                   ∀ x ∈ X, 均满足属性 P(x)
                 </text>
                 <text v-else-if="qType === 'existential'" x="0" y="0" fill="#fbbf24" font-size="24" font-weight="900" text-anchor="middle">
                   ∃ x ∈ X, 使得 P(x) 成立
                 </text>
                 <text v-else x="0" y="0" fill="#fb7185" font-size="24" font-weight="900" text-anchor="middle">
                   ~ (∀ x ∈ X, P(x)) ⇔ ∃ x ∈ X, ~P(x)
                 </text>
              </g>
           </g>

           <defs>
              <marker id="arrow-set" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L0,6 L6,3 z" fill="#10b981" />
              </marker>
           </defs>

        </svg>
      </div>

      <!-- 右侧定理解析 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">逻辑与代数模型</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前状态计算</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'set'">
                     <div class="data-row"><span>集合相对距离</span><span class="hl">{{ offset }}</span></div>
                     <div class="data-row"><span>集合运算模式</span><span class="hl text-green">{{ opName }}</span></div>
                     <div class="data-row"><span>交集元素判定</span><span class="hl text-red">{{ offset < 140 ? '不为空集' : '空集 ∅' }}</span></div>
                  </template>
                  
                  <template v-if="mode === 'condition'">
                     <div class="data-row"><span>A ⊆ B 包含判定</span><span class="hl text-red">{{ condOffset === 0 ? '完全包含 (A ⊆ B)' : '未包含 (A ⊈ B)' }}</span></div>
                     <div class="data-row"><span>A => B (充分)</span><span class="hl text-green">{{ condOffset === 0 ? '为真 (True)' : '为假 (False)' }}</span></div>
                     <div class="data-row"><span>~B => ~A (逆否)</span><span class="hl text-blue">{{ condOffset === 0 ? '为真 (逻辑等价)' : '为假' }}</span></div>
                  </template>

                  <template v-if="mode === 'quantifier'">
                     <div class="data-row"><span>量词类型</span><span class="hl text-green">{{ qType === 'universal' ? '全称 (∀)' : (qType === 'existential' ? '存在 (∃)' : '否定 (~)') }}</span></div>
                     <div class="data-row"><span>满足属性的元素数</span><span class="hl text-blue">{{ satisfiedCount }} / 15</span></div>
                     <div class="data-row"><span>命题真值</span><span class="hl text-red">{{ truthValue ? '真 (True)' : '假 (False)' }}</span></div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>知识点模式选择</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="set">集合运算</el-radio-button>
                 <el-radio-button label="condition">充分必要条件</el-radio-button>
                 <el-radio-button label="quantifier">全称与存在量词</el-radio-button>
               </el-radio-group>
            </div>
            
            <template v-if="mode === 'set'">
               <div class="s-item">
                  <label>运算类型</label>
                  <el-select v-model="op" size="small">
                     <el-option label="并集 (A ∪ B)" value="union"></el-option>
                     <el-option label="交集 (A ∩ B)" value="intersection"></el-option>
                     <el-option label="集合 A 独立" value="none"></el-option>
                  </el-select>
               </div>
               <div class="s-item">
                  <label>拖动改变集合间距关系</label>
                  <el-slider v-model="offset" :min="0" :max="160" :step="10" size="small"></el-slider>
               </div>
            </template>
            
            <template v-if="mode === 'condition'">
               <div class="s-item" style="grid-column: span 2;">
                  <label>调整条件 A 相对于结果 B 的涵盖范围 (当脱离体系时不再充分)</label>
                  <el-slider v-model="condOffset" :min="0" :max="180" :step="10" size="small"></el-slider>
               </div>
            </template>

            <template v-if="mode === 'quantifier'">
               <div class="s-item">
                  <label>量词与命题变更</label>
                  <el-select v-model="qType" size="small">
                     <el-option label="全称命题: ∀x(全绿)" value="universal"></el-option>
                     <el-option label="存在命题: ∃x(有绿)" value="existential"></el-option>
                     <el-option label="全称的否定命题: ~∀" value="negation"></el-option>
                  </el-select>
               </div>
               <div class="s-item">
                  <label>满足属性 P(x) (变成绿色)的元素比例</label>
                  <el-slider v-model="passRate" :min="0" :max="15" :step="1" size="small"></el-slider>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SetTheoryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'set',
      op: 'union',
      offset: 60,
      condOffset: 0,
      qType: 'universal',
      passRate: 15
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'condition') return '充分条件与必要条件：逻辑推导与集合包含关系的等价性';
       if (this.mode === 'quantifier') return '全称量词与存在量词：对集合元素的属性约束判定';
       return '集合的概念及其基本运算（交、并、补）的维恩图表示';
    },
    activeFormula() {
       if (this.mode === 'condition') return 'p ⇒ q ⇔ P ⊆ Q';
       if (this.mode === 'quantifier') return '~(∀x, P(x)) ⇔ ∃x, ~P(x)';
       return 'A ∪ B | A ∩ B | A ⊆ B';
    },
    opName() {
       if (this.op === 'union') return '并集 (A ∪ B)';
       if (this.op === 'intersection') return '交集 (A ∩ B)';
       return '只查看 A';
    },
    fillA() {
       if (this.op === 'union' || this.op === 'none') return 'rgba(56, 189, 248, 0.5)';
       return 'rgba(56, 189, 248, 0.15)';
    },
    fillB() {
       if (this.op === 'union') return 'rgba(244, 63, 94, 0.5)';
       return 'rgba(244, 63, 94, 0.15)';
    },
    satisfiedCount() {
       return this.passRate;
    },
    truthValue() {
       if (this.qType === 'universal') return this.passRate === 15;
       if (this.qType === 'existential') return this.passRate > 0;
       return this.passRate < 15; // negation of universal is true if not all are true
    },
    tipDesc() {
       if (this.mode === 'condition') return '判断充分必要条件最直观的办法就是画集合！如果条件 A 代表的事件集合能“全部塞进”结果 B 中（即 A 是 B 的子集），由于 A 发生必然落在 B 内，所以 A 就是 B 的【充分条件】。同时，要发生 A 首先得在 B 中，故 B 是 A 的【必要条件】。';
       if (this.mode === 'quantifier') return '“任意（∀）”代表无一例外，必须所有元素翻绿，命题才为真，只要存在一个红子，全称命题即被证伪。“存在（∃）”代表只要能抓出一个绿子，命题就为真。这也对应了德·摩根定律在逻辑命题中的投射：对全称命题的否定，就是寻找一个特例。';
       return '维恩图由约翰·维恩发明。蓝色覆盖区域、红色覆盖区域以及它们交叠而生成的绿色区域，分别代表了独立元素的归属、以及同时具备两项特征的重合子集。改变集合距离可以直观看到交集如何变成空集（∅）。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/充分|必要|包含/.test(kp)) {
         this.mode = 'condition'; 
         this.condOffset = 0;
      } else if (/量词|全称|存在/.test(kp)) {
         this.mode = 'quantifier';
         this.qType = 'universal';
      } else {
         this.mode = 'set';
      }
    },
    isSatisfied(i) {
       // Since the question is to just color them based on a slider count:
       return i <= this.passRate;
    }
  }
}
</script>

<style scoped lang="scss">
.set-theory-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #fb7185; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
